import React, { useEffect, useState } from "react";
import {
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const Settings = () => {
  const [username, setUsername] = useState("");
  const [settings, setSettings] = useState({
    trainerCode: "",
    team: "",
    publicProfile: false,
  });
  const [passwords, setPasswords] = useState({ old: "", new1: "", new2: "" });
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const [deletePrompt, setDeletePrompt] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(5);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    axios.get("/api/users/me", { headers: authHeader() }).then((res) => {
      setUsername(res.data.username);
    });
    axios.get("/api/users/settings", { headers: authHeader() }).then((res) => {
      setSettings((prev) => ({ ...prev, ...res.data }));
    });
  }, []);

  const isValidTrainerCode = (code) => /^\d{12}$/.test(code);
  const isValidTeam = (team) => ["Blau", "Rot", "Gelb"].includes(team);

  const savePublicSettings = () => {
    if (!isValidTrainerCode(settings.trainerCode)) {
      showSnack("Trainercode muss genau 12 Ziffern enthalten.", "error");
      return;
    }
    if (!isValidTeam(settings.team)) {
      showSnack("Bitte ein gültiges Team auswählen.", "error");
      return;
    }
    axios
      .post("/api/users/settings", settings, { headers: authHeader() })
      .then(() => showSnack("Einstellungen gespeichert."))
      .catch(() => showSnack("Fehler beim Speichern.", "error"));
  };

  const savePassword = () => {
    const { old, new1, new2 } = passwords;
    if (!old || !new1 || !new2) {
      showSnack("Bitte alle Passwortfelder ausfüllen.", "error");
      return;
    }
    if (new1.length < 6) {
      showSnack("Neues Passwort muss mindestens 6 Zeichen lang sein.", "error");
      return;
    }
    if (new1 !== new2) {
      showSnack("Neue Passwörter stimmen nicht überein.", "error");
      return;
    }
    if (old === new1) {
      showSnack("Das neue Passwort muss sich vom alten unterscheiden.", "error");
      return;
    }
    axios
      .post(
        "/api/users/change-password",
        { oldPassword: old, newPassword: new1 },
        { headers: authHeader() }
      )
      .then(() => {
        showSnack("Passwort erfolgreich geändert.");
        setPasswords({ old: "", new1: "", new2: "" });
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Fehler beim Ändern des Passworts.";
        showSnack(msg, "error");
      });
  };

  const deleteAccount = () => {
    axios
      .delete("/api/users/delete-account", { headers: authHeader() })
      .then(() => {
        localStorage.removeItem("token");
        window.location.href = "/";
      })
      .catch(() => showSnack("Fehler beim Löschen des Accounts.", "error"));
  };

  const handleDeleteClick = () => {
    setDeletePrompt(true);
    setDeleteCountdown(5);
    const interval = setInterval(() => {
      setDeleteCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formattedTrainerCode = settings.trainerCode
    .replace(/\D/g, "")
    .slice(0, 12)
    .replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3");

  return (
    <Container
      maxWidth="sm"
      sx={{
        pt: 3,
        pb: { xs: "88px", sm: "72px" },
      }}
    >
      <Typography variant="h5" gutterBottom>
        Einstellungen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Account: <strong>{username}</strong>
      </Typography>

      {/* Öffentliche Einstellungen */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" gutterBottom>
          Öffentliches Profil
        </Typography>

        <TextField
          label="Trainercode"
          fullWidth
          margin="normal"
          value={formattedTrainerCode}
          onChange={(e) => {
            const clean = e.target.value.replace(/\D/g, "").slice(0, 12);
            setSettings({ ...settings, trainerCode: clean });
          }}
          helperText="12-stelliger Trainercode (z. B. 1234-5678-9012)"
          inputProps={{ inputMode: "numeric" }}
        />

        <Select
          fullWidth
          value={settings.team}
          onChange={(e) => setSettings({ ...settings, team: e.target.value })}
          displayEmpty
          sx={{ mt: 1, mb: 0.5 }}
        >
          <MenuItem value="">Team auswählen</MenuItem>
          <MenuItem value="Blau">Blau (Mystic)</MenuItem>
          <MenuItem value="Rot">Rot (Valor)</MenuItem>
          <MenuItem value="Gelb">Gelb (Instinct)</MenuItem>
        </Select>

        <FormControlLabel
          control={
            <Switch
              checked={settings.publicProfile}
              onChange={(e) =>
                setSettings({ ...settings, publicProfile: e.target.checked })
              }
            />
          }
          label="Profil öffentlich sichtbar"
          sx={{ mt: 1.5, display: "flex" }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Andere Trainer können dann dein Profil, Pokédex und Arenen einsehen.
        </Typography>

        <Button variant="contained" onClick={savePublicSettings}>
          Speichern
        </Button>
      </Paper>

      {/* Passwort ändern */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" gutterBottom>
          Passwort ändern
        </Typography>

        <TextField
          label="Aktuelles Passwort"
          type={showOldPw ? "text" : "password"}
          fullWidth
          margin="normal"
          value={passwords.old}
          onChange={(e) => setPasswords({ ...passwords, old: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOldPw((p) => !p)} edge="end">
                  {showOldPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Neues Passwort"
          type={showNewPw ? "text" : "password"}
          fullWidth
          margin="normal"
          value={passwords.new1}
          onChange={(e) => setPasswords({ ...passwords, new1: e.target.value })}
          helperText="Mindestens 6 Zeichen"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPw((p) => !p)} edge="end">
                  {showNewPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Neues Passwort wiederholen"
          type={showNewPw ? "text" : "password"}
          fullWidth
          margin="normal"
          value={passwords.new2}
          onChange={(e) => setPasswords({ ...passwords, new2: e.target.value })}
          error={passwords.new2.length > 0 && passwords.new1 !== passwords.new2}
          helperText={
            passwords.new2.length > 0 && passwords.new1 !== passwords.new2
              ? "Passwörter stimmen nicht überein"
              : ""
          }
        />

        <Button
          variant="contained"
          sx={{ mt: 1 }}
          onClick={savePassword}
          disabled={!passwords.old || !passwords.new1 || !passwords.new2}
        >
          Passwort ändern
        </Button>
      </Paper>

      {/* Account löschen */}
      <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "error.light" }}>
        <Typography variant="h6" gutterBottom color="error">
          Gefahrenzone
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Das Löschen deines Accounts ist dauerhaft und kann nicht rückgängig gemacht werden.
        </Typography>
        <Button color="error" variant="outlined" onClick={handleDeleteClick}>
          Account löschen
        </Button>
      </Paper>

      {/* Schritt 1: Sind Sie sicher? */}
      <Dialog open={deletePrompt} onClose={() => setDeletePrompt(false)}>
        <DialogTitle>Account wirklich löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Alle deine Daten (Pokédex, Arenen, Medaillen) werden unwiderruflich gelöscht.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePrompt(false)}>Abbrechen</Button>
          <Button
            disabled={deleteCountdown > 0}
            onClick={() => {
              setDeletePrompt(false);
              setDeleteConfirm(true);
            }}
            color="error"
          >
            Bestätigen {deleteCountdown > 0 ? `(${deleteCountdown}s)` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schritt 2: Letzte Bestätigung */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Letzte Bestätigung</DialogTitle>
        <DialogContent>
          <Typography>
            ⚠️ Alle Daten werden <strong>unwiderruflich</strong> gelöscht.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={deleteAccount}>
            Jetzt endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
